
import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import Select from '../../../components/base/Select';

interface User {
  id: string;
  name: string;
  email: string;
  password?: string;
  role: 'admin' | 'agent' | 'call_center';
  created_at: string;
  active: boolean;
  assigned_agents?: string[];
  company_id?: string;
}

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showUserDetails, setShowUserDetails] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'agent' as 'admin' | 'agent' | 'call_center',
    assignedAgents: [] as string[], // Pour les call centers
    active: true
  });
  const [error, setError] = useState<string | null>(null);

  const loadUsers = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/users-management?action=list`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        }
      });

      const data = await response.json();
      if (data.success) {
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        // Filtrer les utilisateurs par société
        const companyUsers = data.users.filter((user: any) => user.company_id === currentUser.company_id);
        setUsers(companyUsers);
      } else {
        setError(data.error || 'Erreur lors du chargement des utilisateurs');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (userData: any) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/users-management`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...userData,
          company_id: currentUser.company_id // Associer à la société courante
        })
      });
    } catch (error) {
      console.error('Erreur lors de la création d\'un utilisateur:', error);
      setError('Erreur de création d\'utilisateur');
    }
  };

  const updateUser = async (userData: any) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/users-management`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...userData,
          company_id: currentUser.company_id // Maintenir l'association à la société
        })
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'utilisateur:', error);
      setError('Erreur de mise à jour d\'utilisateur');
    }
  };

  // Charger les utilisateurs au montage du composant
  useEffect(() => {
    loadUsers();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      
      const userData = {
        name: formData.name,
        email: formData.email,
        password: formData.password || undefined,
        role: formData.role,
        assigned_agents: formData.role === 'call_center' ? formData.assignedAgents : null,
        active: formData.active,
        company_id: currentUser.company_id // IMPORTANT: Ajouter company_id
      };

      if (editingUser) {
        // Modifier un utilisateur existant
        const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/users-management`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ id: editingUser.id, ...userData })
        });

        const result = await response.json();
        if (result.success) {
          await loadUsers(); // Recharger la liste
          alert('Utilisateur modifié avec succès !');
        } else {
          alert('Erreur: ' + result.error);
        }
      } else {
        // Créer un nouvel utilisateur
        const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/users-management`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify(userData)
        });

        const result = await response.json();
        if (result.success) {
          await loadUsers(); // Recharger la liste
          alert('Utilisateur créé avec succès !');
        } else {
          alert('Erreur: ' + result.error);
        }
      }

      setShowUserForm(false);
      setEditingUser(null);
      resetForm();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role as 'admin' | 'agent' | 'call_center',
      assignedAgents: user.assigned_agents || [],
      active: user.active
    });
    setShowUserForm(true);
  };

  const handleViewDetails = (user: User) => {
    setShowUserDetails(user);
  };

  const toggleUserStatus = async (userId: string, currentStatus: boolean) => {
    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/users-management`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          id: userId,
          active: !currentStatus
        })
      });

      const result = await response.json();
      if (result.success) {
        await loadUsers(); // Recharger la liste
      } else {
        alert('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.')) {
      setLoading(true);
      try {
        const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/users-management`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ id: userId })
        });

        const result = await response.json();
        if (result.success) {
          await loadUsers(); // Recharger la liste
        } else {
          alert('Erreur: ' + result.error);
        }
      } catch (error) {
        console.error('Erreur:', error);
        alert('Erreur de connexion au serveur');
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'agent',
      assignedAgents: [],
      active: true
    });
  };

  if (loading && users.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Chargement des utilisateurs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des utilisateurs</h1>
          <p className="text-gray-600">{users.length} utilisateurs au total</p>
        </div>
        <Button onClick={() => setShowUserForm(true)}>
          <i className="ri-user-add-line mr-2"></i>
          Nouvel utilisateur
        </Button>
      </div>

      {/* Message d'information */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <div className="flex items-center">
          <i className="ri-database-2-line text-green-600 mr-2"></i>
          <p className="text-green-800 text-sm">
            <strong>Supabase connecté !</strong> Tous les utilisateurs sont maintenant synchronisés et peuvent se connecter
          </p>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-shield-user-line text-blue-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Administrateurs</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter(user => user.role === 'admin' && user.active).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-line text-green-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Agents actifs</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter(user => user.role === 'agent' && user.active).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-unfollow-line text-red-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Utilisateurs inactifs</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter(user => !user.active).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des utilisateurs */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agents Assignés
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date de création
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-gray-600"></i>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.role === 'admin' 
                        ? 'bg-red-100 text-red-800'
                        : user.role === 'call_center'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {user.role === 'admin' ? 'Administrateur' : 
                       user.role === 'call_center' ? 'Call Center' : 'Agent'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.role === 'call_center' && user.assigned_agents?.length ? (
                      <div>
                        <span className="text-xs text-gray-500">
                          {user.assigned_agents.length} agent(s) assigné(s)
                        </span>
                        <div className="text-xs text-gray-400 mt-1">
                          {user.assigned_agents
                            .map(agentId => users.find(u => u.id === agentId)?.name)
                            .filter(Boolean)
                            .slice(0, 2)
                            .join(', ')}
                          {user.assigned_agents.length > 2 && ` +${user.assigned_agents.length - 2}`}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user.active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button 
                      onClick={() => handleViewDetails(user)}
                      className="text-blue-600 hover:text-blue-900 cursor-pointer mr-3"
                      title="Voir les détails"
                    >
                      <i className="ri-eye-line"></i>
                    </button>
                    <button 
                      onClick={() => handleEdit(user)}
                      className="text-green-600 hover:text-green-900 cursor-pointer mr-3"
                      title="Modifier"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button 
                      onClick={() => toggleUserStatus(user.id, user.active)}
                      className={`mr-3 cursor-pointer ${
                        user.active 
                          ? 'text-orange-600 hover:text-orange-900' 
                          : 'text-green-600 hover:text-green-900'
                      }`}
                      title={user.active ? 'Désactiver' : 'Activer'}
                    >
                      <i className={user.active ? 'ri-pause-circle-line' : 'ri-play-circle-line'}></i>
                    </button>
                    <button 
                      onClick={() => deleteUser(user.id)}
                      className="text-red-600 hover:text-red-900 cursor-pointer"
                      title="Supprimer"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de formulaire utilisateur */}
      {showUserForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {editingUser ? "Modifier l'utilisateur" : 'Nouvel utilisateur'}
              </h2>
              <button 
                onClick={() => {
                  setShowUserForm(false);
                  setEditingUser(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom complet *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Entrez le nom complet"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Entrez l'adresse email"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe *
                </label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Entrez le mot de passe"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rôle *
                </label>
                <Select
                  options={[
                    { value: 'agent', label: 'Agent de téléprospection' },
                    { value: 'call_center', label: 'Call Center (Superviseur)' },
                    { value: 'admin', label: 'Administrateur' }
                  ]}
                  value={formData.role}
                  onChange={(value) => setFormData({ ...formData, role: value as 'admin' | 'agent' | 'call_center' })}
                />
              </div>

              {/* Section d'assignation d'agents pour les call centers */}
              {formData.role === 'call_center' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agents assignés
                  </label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-3">
                      Sélectionnez les agents dont ce call center pourra voir les clients
                    </p>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {users
                        .filter(user => user.role === 'agent' && user.active)
                        .map(agent => (
                          <label key={agent.id} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.assignedAgents.includes(agent.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({
                                    ...formData,
                                    assignedAgents: [...formData.assignedAgents, agent.id]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    assignedAgents: formData.assignedAgents.filter(id => id !== agent.id)
                                  });
                                }
                              }}
                              className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">{agent.name} ({agent.email})</span>
                          </label>
                        ))}
                    </div>
                    {users.filter(user => user.role === 'agent' && user.active).length === 0 && (
                      <p className="text-sm text-gray-500 italic">Aucun agent disponible pour l'assignation</p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <Button 
                  type="button"
                  variant="secondary" 
                  onClick={() => {
                    setShowUserForm(false);
                    setEditingUser(null);
                    resetForm();
                  }}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={loading}>
                  <i className="ri-save-line mr-2"></i>
                  {loading ? 'Sauvegarde...' : (editingUser ? 'Modifier' : 'Créer')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de détails utilisateur */}
      {showUserDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Détails utilisateur
              </h2>
              <button 
                onClick={() => setShowUserDetails(null)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-user-line text-gray-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{showUserDetails.name}</h3>
                  <p className="text-gray-500">{showUserDetails.email}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                      showUserDetails.role === 'admin' 
                        ? 'bg-red-100 text-red-800' 
                        : showUserDetails.role === 'call_center'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {showUserDetails.role === 'admin' ? 'Administrateur' : 
                       showUserDetails.role === 'call_center' ? 'Call Center' : 'Agent'}
                    </span>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                      showUserDetails.active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {showUserDetails.active ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date de création</label>
                    <p className="text-sm text-gray-900">
                      {new Date(showUserDetails.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ID utilisateur</label>
                    <p className="text-sm text-gray-900 font-mono">#{showUserDetails.id.slice(0, 8)}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <Button variant="secondary" onClick={() => setShowUserDetails(null)}>
                Fermer
              </Button>
              <Button onClick={() => {
                setShowUserDetails(null);
                handleEdit(showUserDetails);
              }}>
                <i className="ri-edit-line mr-2"></i>
                Modifier
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
